/**
 * Product Combiner Nested - Two-level hierarchical product selection
 * Primary selection (e.g., Blade Type) filters secondary options (e.g., Handle Color)
 */

if (!customElements.get('product-combiner-nested')) {
  class ProductCombinerNested extends HTMLElement {
    constructor() {
      super();
      this.primaryButtons = this.querySelectorAll('.product-combiner-nested__option');
      this.secondarySwatches = this.querySelectorAll('.product-combiner-nested__swatch');
      this.dataScript = this.querySelector('.product-combiner-nested__data');
      this.data = null;
      
      if (this.dataScript) {
        try {
          this.data = JSON.parse(this.dataScript.textContent);
        } catch (e) {
          console.error('Failed to parse product combiner data:', e);
        }
      }
      
      this.currentPrimary = this.dataset.currentPrimary;
      this.currentSecondary = this.dataset.currentSecondary;
      
      this.init();
    }
    
    init() {
      this.bindPrimaryButtons();
      this.bindSecondarySwatches();
      this.bindKeyboardNavigation();
    }
    
    bindPrimaryButtons() {
      this.primaryButtons.forEach(button => {
        button.addEventListener('click', (e) => {
          e.preventDefault();
          const primaryValue = button.dataset.primary;
          
          if (primaryValue === this.currentPrimary) return;
          
          this.selectPrimary(primaryValue, button);
        });
      });
    }
    
    selectPrimary(primaryValue, clickedButton) {
      // Update active state on primary buttons
      this.primaryButtons.forEach(btn => {
        btn.classList.remove('product-combiner-nested__option--active');
        btn.setAttribute('aria-pressed', 'false');
      });
      clickedButton.classList.add('product-combiner-nested__option--active');
      clickedButton.setAttribute('aria-pressed', 'true');
      
      // Update primary label
      const primaryLabel = this.querySelector('.product-combiner-nested__group--primary .product-combiner-nested__label-value');
      if (primaryLabel) {
        primaryLabel.textContent = clickedButton.querySelector('.product-combiner-nested__option-text').textContent;
      }
      
      // Filter secondary swatches
      this.filterSecondarySwatches(primaryValue);
      
      // Find the first available product with this primary and navigate
      if (this.data) {
        const matchingProduct = this.data.products.find(p => 
          p.primary === primaryValue && p.available
        ) || this.data.products.find(p => p.primary === primaryValue);
        
        if (matchingProduct) {
          // Navigate to the new product
          window.location.href = matchingProduct.url;
        }
      }
    }
    
    filterSecondarySwatches(primaryValue) {
      let firstVisible = null;
      
      this.secondarySwatches.forEach(swatch => {
        const swatchPrimary = swatch.dataset.primary;
        
        if (swatchPrimary === primaryValue) {
          swatch.style.display = '';
          if (!firstVisible) firstVisible = swatch;
        } else {
          swatch.style.display = 'none';
        }
      });
      
      // Update secondary label if we have a visible swatch
      if (firstVisible) {
        const secondaryLabel = this.querySelector('.product-combiner-nested__group--secondary .product-combiner-nested__label-value');
        const swatchLabel = firstVisible.querySelector('.product-combiner-nested__swatch-label');
        if (secondaryLabel && swatchLabel) {
          secondaryLabel.textContent = swatchLabel.textContent;
        }
      }
    }
    
    bindSecondarySwatches() {
      this.secondarySwatches.forEach(swatch => {
        // Preload on hover
        swatch.addEventListener('mouseenter', () => {
          const href = swatch.getAttribute('href');
          if (href && !document.querySelector(`link[href="${href}"]`)) {
            const link = document.createElement('link');
            link.rel = 'prefetch';
            link.href = href;
            document.head.appendChild(link);
          }
        });
      });
    }
    
    bindKeyboardNavigation() {
      // Primary buttons keyboard nav
      const primaryContainer = this.querySelector('.product-combiner-nested__options--primary');
      if (primaryContainer) {
        primaryContainer.addEventListener('keydown', (e) => {
          this.handleKeyNav(e, this.primaryButtons);
        });
      }
      
      // Secondary swatches keyboard nav
      const secondaryContainer = this.querySelector('.product-combiner-nested__options--secondary');
      if (secondaryContainer) {
        secondaryContainer.addEventListener('keydown', (e) => {
          const visibleSwatches = Array.from(this.secondarySwatches).filter(s => s.style.display !== 'none');
          this.handleKeyNav(e, visibleSwatches);
        });
      }
    }
    
    handleKeyNav(e, elements) {
      const currentIndex = Array.from(elements).indexOf(document.activeElement);
      if (currentIndex === -1) return;
      
      let newIndex;
      
      switch (e.key) {
        case 'ArrowRight':
        case 'ArrowDown':
          e.preventDefault();
          newIndex = (currentIndex + 1) % elements.length;
          elements[newIndex].focus();
          break;
        case 'ArrowLeft':
        case 'ArrowUp':
          e.preventDefault();
          newIndex = (currentIndex - 1 + elements.length) % elements.length;
          elements[newIndex].focus();
          break;
        case 'Home':
          e.preventDefault();
          elements[0].focus();
          break;
        case 'End':
          e.preventDefault();
          elements[elements.length - 1].focus();
          break;
      }
    }
  }
  
  customElements.define('product-combiner-nested', ProductCombinerNested);
}
